/**
 * WASM decoders, used only where the browser's own decoder won't do.
 *
 * `createImageBitmap` is always tried first — it is faster, it costs no bundle
 * weight, and it reads formats no WASM codec here can (BMP, and HEIC on
 * Safari). These are the fallback for the two cases it leaves open:
 *
 * - **JPEG XL and QOI**, which no browser decodes today. This is the only way
 *   those uploads get compressed at all.
 * - **AVIF and WebP on an older browser**, where native support is missing but
 *   the file is still perfectly valid.
 *
 * JPEG is listed for completeness — the native path handles it everywhere, so
 * in practice that entry never loads. PNG has no entry at all: there is no
 * browser that can't read one, and the encode side dropped `@jsquash/png`
 * (OxiPNG encodes straight from pixels), so listing it would drag a whole
 * codec back in for a case that cannot happen.
 *
 * Each import is lazy: nothing here reaches the bundle until a file actually
 * needs it.
 */
type CodecDecoder = (buffer: ArrayBuffer) => Promise<ImageData | null>;

const DECODER_BY_MIME_TYPE: Record<string, () => Promise<CodecDecoder>> = {
  "image/jxl": async () => (await import("@jsquash/jxl/decode")).default,
  "image/qoi": async () => (await import("@jsquash/qoi/decode")).default,
  "image/avif": async () => (await import("@jsquash/avif/decode")).default,
  "image/webp": async () => (await import("@jsquash/webp/decode")).default,
  "image/jpeg": async () => (await import("@jsquash/jpeg/decode")).default,
  "image/jpg": async () => (await import("@jsquash/jpeg/decode")).default,
};

export const decodeWithCodec = async (
  file: File
): Promise<ImageData | null> => {
  const loadDecoder = DECODER_BY_MIME_TYPE[file.type.toLowerCase()];

  if (!loadDecoder) {
    return null;
  }

  const decode = await loadDecoder();

  return decode(await file.arrayBuffer());
};
